import React from 'react';

import get from 'lodash/get';
import keys from 'lodash/keys';
import first from 'lodash/first';
import values from 'lodash/values';

import {
  sendRequest
} from 'zoomforth_zfutils/xhr_tools.js';

import {
  CheckCircleIcon
} from 'zoomforth_component_lib/app_modern_icons.jsx';

import {
  getPageId
} from 'zoomforth_codesbane_editor/reducers/index.js';

import {
  makeSaveId,
  unblockSave,
  pendSave,
  sendSave,
  commitSave,
  failSave
} from 'zoomforth_shared_management/action_creators/saving.js';

import {
  success as showNotificationSuccess
} from 'zoomforth_app_basics/action_creators/notifications.js';

import {
  getPendingSave,
  getSendingOrBlockingSave
} from 'zoomforth_codesbane_editor/reducers/index.js';

import {
  getTranslationsByIso,
  getTranslationsIsEnabled,
  getTranslationsDefaultLanguageIso,
  getTranslationsCurrentLanguageIso,
  getTranslationsSelectedLanguageIsos
} from 'zoomforth_codesbane_viewer/reducers/index.js';

import {
  SET_TEMPORARY_PAGE_TITLE,
  SET_TRANSLATIONS_IS_ENABLED,
  SET_TRANSLATIONS_DEFAULT_LANGUAGE_ISO,
  SET_TRANSLATIONS_CURRENT_LANGUAGE_ISO,
  SET_TRANSLATIONS_SELECTED_LANGUAGE_ISOS,
  SET_IS_TRANSLATIONS_EDITOR_OPEN,
  SET_TRANSLATIONS_APPLIED_LANGUAGES,
  SET_TRANSLATIONS_STATUS_FOR_ISO,
  SET_TRANSLATION_DATA_FOR_ISO,
  MASK_PAGE_ENTITIES_STATE,
  MASK_CUSTOM_FOOTER_STATE
} from '../action_types.js';

import TextTranslationEditorModal
  from 'zoomforth_codesbane_editor/exterior_editor/editor_modals/text_translation_modal_container.jsx';

import {
  setupAppContainerTranslationClassName
} from 'zoomforth_codesbane_viewer/lib/setup_utils.js';

import {
  clean_state_obj
} from 'translations/filter_fields.js';

import {
  showModal
} from './editor_modals.js';

import {
  ensureThrottledPageConfigSaving
} from './config.js';

import {
  closeAllEditingFeatures
} from './misc.js';

import {
  notifyErrorMaybeLostData
} from './misc.js';

import {
  markAsDraft
} from './versioning.js';

import {
  savePageMetaAttrs
} from './pageMeta.js';


export const TRANSLATION_STARTED_STATUS = 'TRANSLATION_STARTED_STATUS';
export const TRANSLATION_COMPLETED_STATUS = 'TRANSLATION_COMPLETED_STATUS';


const _make_state_obj = (state)=> {
  const filteredState = {
    ...state.pageEntities,
    customFooterHtml: get(state, 'theme.customFooterHtml', '')
  };

  return clean_state_obj(filteredState);
};


/**
 * Translate Text Request.
 *
 * This is what actually starts the pipeline to translate a site.
 */
export const translateSite = ()=>
  (dispatch, getState) => {
    const state = getState();
    const srcLang = getTranslationsDefaultLanguageIso(state);
    const targetLangs = getTranslationsSelectedLanguageIsos(state);
    const siteId = getPageId(state);

    for (const lang of targetLangs) {
      dispatch(setTranslationStatusStarted(lang));
    }

    sendRequest({
      url: '/rpc/translations/start',
      method: 'POST',
      params: {
        site_id: siteId,
        src_lang: srcLang,
        target_langs: targetLangs,
        state_obj: _make_state_obj(state),
      },
      onSuccess: () => {
        dispatch(showNotificationSuccess({
          icon: <CheckCircleIcon />,
          message: 'Your translation has successfully started'
        }));

        dispatch(markAsDraft());
        dispatch(savePageMetaAttrs(['isDraft']));
      },
      onError: ({ status, responseText })=> {
        dispatch(notifyErrorMaybeLostData(status, responseText));
      }
    });
  };


/**
 * Translate Text Request.
 *
 * This is what actually starts the pipeline to translate a site.
 */
export const translateText = (text, onComplete)=>
  (dispatch, getState) => {
    const state = getState();
    const srcLang = getTranslationsDefaultLanguageIso(state);
    const targetLang = getTranslationsCurrentLanguageIso(state);

    sendRequest({
      url: '/rpc/translations/text',
      method: 'POST',
      params: {
        src_lang: srcLang,
        target_lang: targetLang,
        text: text
      },
      onSuccess: ({ translated_text }) => {
        onComplete(translated_text);
      },
      onError: ({ status, responseText })=> {
        dispatch(notifyErrorMaybeLostData(status, responseText));
      }
    });
  };

/**
 * Open the text translation single item modal
 */
export const openTextTranslationModal = (
  text,
  onTranslationSave,
  {
    froalaConfig,
    shouldUseFroalaComponent,
    shouldUseTextAreaComponent
  } = {}
)=>
  (dispatch)=> {
    const props = { 
      text,
      froalaConfig,
      shouldUseFroalaComponent,
      shouldUseTextAreaComponent,
      onTranslationSave,
      onMount: (onComplete)=> dispatch(translateText(text, onComplete)),
    };

    dispatch(showModal(TextTranslationEditorModal, props));
  };


/**
 * Update translations data after an edit has occurred
 */
export const updateTranslationsData = ()=>
  (dispatch, getState) => {
    const state = getState();
    const siteId = getPageId(state);
    const srcLang = getTranslationsDefaultLanguageIso(state);
    const currentLang = getTranslationsCurrentLanguageIso(state);

    const saveId = makeSaveId();
    dispatch(sendSave(`translation__${currentLang}`, saveId));
    dispatch(markAsDraft());

    sendRequest({
      url: '/rpc/translations/update_state',
      method: 'POST',
      params: {
        site_id: siteId,
        src_lang: srcLang,
        current_lang: currentLang,
        state_obj: _make_state_obj(state),
      },
      onSuccess: (response) => {
        dispatch(commitSave(saveId, { blockNext:true }));

        setTimeout(()=> {
          if (getPendingSave(getState(), `translation__${currentLang}`)) {
            dispatch(updateTranslationsData()); // Also unblocks
          } else {
            dispatch(unblockSave(`translation__${currentLang}`));
          }

        }, 1000);

        dispatch(setTranslationsDataForIso(
          first(keys(response)),
          first(values(response))
        ));

        dispatch(savePageMetaAttrs(['isDraft']));
      },
      onError: ({ status, responseText })=> {
        dispatch(failSave(saveId));
        dispatch(notifyErrorMaybeLostData(status, responseText));
      }
    });
  };

/**
 * Set the translations data for a specific ISO code
 */
export const setTranslationsDataForIso = (iso, translationsData)=> {
  return {
    type: SET_TRANSLATION_DATA_FOR_ISO,
    iso: iso,
    data: translationsData
  };
};

/**
 * Throttle the translation save
 */
export const ensureThrottledTranslationUpdate = ()=>
  (dispatch, getState)=> {
    if (getTranslationsIsEnabled(getState())) {
      const currentLang = getTranslationsCurrentLanguageIso(getState());

      dispatch(pendSave(`translation__${currentLang}`, `Update translation "${currentLang}"`));

      if (!getSendingOrBlockingSave(getState(), `translation__${currentLang}`)) {
        dispatch(updateTranslationsData());
      }
    }

  };

/**
 * Set whether translations is enabled on the current site version
 */
export const setTranslationsIsEnabled = (isEnabled)=>
  (dispatch, getState)=> {
    dispatch({
      type: SET_TRANSLATIONS_IS_ENABLED,
      value: isEnabled
    });

    const currentLangIso = getTranslationsCurrentLanguageIso(getState());

    if (isEnabled) {
      setupAppContainerTranslationClassName(currentLangIso);
    }

    dispatch(ensureThrottledPageConfigSaving());
  };

/**
 * Set default language ISO on the current site version
 */
export const setTranslationsDefaultLanguageIso = (iso)=>
  (dispatch)=> {
    dispatch({
      type: SET_TRANSLATIONS_DEFAULT_LANGUAGE_ISO,
      value: iso
    });

    dispatch(ensureThrottledPageConfigSaving());
  };

/**
 * Set applied languages on the current site version
 */
export const updateTranslationsAppliedLanguages = (appliedLanguages)=>
  (dispatch)=> {
    dispatch({
      type: SET_TRANSLATIONS_APPLIED_LANGUAGES,
      value: appliedLanguages
    });

    dispatch(ensureThrottledPageConfigSaving());
  };

/**
 * Set current language ISO for the editor. This is what's used to know if
 * we're editing other languages.
 */
export const setTranslationsCurrentLanguageIso = (iso)=>
  (dispatch, getState)=> {
    dispatch({
      type: SET_TRANSLATIONS_CURRENT_LANGUAGE_ISO,
      value: iso
    });

    dispatch(closeAllEditingFeatures());

    const translations = getTranslationsByIso(getState(), iso);
    const { customFooterHtml, ...newPageEntities } = translations;
    const { title } = newPageEntities;

    dispatch({ type: MASK_PAGE_ENTITIES_STATE, newPageEntities });
    dispatch({ type: MASK_CUSTOM_FOOTER_STATE, customFooterHtml });
    dispatch({ type: SET_TEMPORARY_PAGE_TITLE, pageTitle: title });

    setupAppContainerTranslationClassName(iso);
  };

/**
 * Set the STARTED status for a translation ISO
 */
export const setTranslationStatusStarted = (iso)=> {
  return {
    type: SET_TRANSLATIONS_STATUS_FOR_ISO,
    iso: iso,
    status: TRANSLATION_STARTED_STATUS
  };
};

/**
 * Set the COMPLETED status for a translation ISO
 */
export const setTranslationStatusCompleted = (iso)=> {
  return {
    type: SET_TRANSLATIONS_STATUS_FOR_ISO,
    iso: iso,
    status: TRANSLATION_COMPLETED_STATUS
  };
};

/**
 * Set selected language ISOs for the editor. This is what's used to set the
 * languages the user wants to translate to.
 */
export const setTranslationsSelectedLanguageIsos = (isos)=> {
  return {
    type: SET_TRANSLATIONS_SELECTED_LANGUAGE_ISOS,
    value: isos
  };
};

/**
 * Open the translation editor sidebar
 */
export const openTranslationsEditor = ()=> {
  return {
    type: SET_IS_TRANSLATIONS_EDITOR_OPEN,
    isOpen: true
  };
};

/**
 * Close the translation editor sidebar
 */
export const closeTranslationsEditor = ()=> {
  return {
    type: SET_IS_TRANSLATIONS_EDITOR_OPEN,
    isOpen: false
  };
};
